// API client for the WhatsApp backend. The browser only ever talks to our backend, which
// talks to Meta. Its own variable: VITE_API_BASE_URL is the main ERP backend, not this one.
// Staging: https://staging.inconelpipestube.com/whatsapp (same domain, so the login cookie goes along).
const API_BASE_URL = import.meta.env.VITE_WHATSAPP_API_BASE_URL || (import.meta.env.DEV ? 'http://localhost:4200' : '');

async function request(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    // Sends the ERP session cookie; the backend checks it on every call.
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });

  return readJson(response);
}

// The backend always answers JSON. Anything else, such as the dashboard's own index.html coming
// back because the WhatsApp service isn't running behind the proxy, is an outage. Treating it as
// "no data" made the screens look like an empty account.
async function readJson(response) {
  const isJson = (response.headers.get('content-type') || '').includes('application/json');
  const body = isJson ? await response.json().catch(() => null) : null;
  if (!response.ok) throw new Error(body?.error || body?.message || `WhatsApp service error (${response.status})`);
  if (!isJson) throw new Error('The WhatsApp service is not reachable right now. Please try again in a few minutes.');
  return body;
}

// --- Send ---
export const sendWhatsAppMessage = ({ to, templateName, languageCode, variables, text, campaignId }) =>
  request('/api/whatsapp/send', {
    method: 'POST',
    body: JSON.stringify({ to, templateName, languageCode, variables, text, campaignId })
  });

// --- Messages (received, sent, delivered, read, bounced — filter via query params) ---
export const fetchWhatsAppMessages = ({ phone, direction, status, limit = 50 } = {}) => {
  const params = new URLSearchParams();
  if (phone) params.set('phone', phone);
  if (direction) params.set('direction', direction);
  if (status) params.set('status', status);
  if (limit) params.set('limit', String(limit));
  return request(`/api/whatsapp/messages?${params.toString()}`);
};

// --- Records: clicks ---
export const fetchWhatsAppClicks = (limit = 50) =>
  request(`/api/whatsapp/clicks?limit=${limit}`);

// --- Records: blocked numbers ---
export const fetchWhatsAppBlocked = (limit = 50) =>
  request(`/api/whatsapp/blocked?limit=${limit}`);

export const unblockWhatsAppNumber = (phone) =>
  request(`/api/whatsapp/blocked/${encodeURIComponent(phone)}`, { method: 'DELETE' });

// --- Stats / dashboard summary ---
export const fetchWhatsAppStats = () => request('/api/whatsapp/stats');

// --- Templates (created, edited and deleted on Meta by the backend) ---
// Sent as multipart: the template as a JSON field, plus `sample` — the image/video/PDF Meta
// reviews when the template has a media header.
async function sendTemplateForm(method, endpoint, { sample, ...template }) {
  const form = new FormData();
  form.append('template', JSON.stringify(template));
  if (sample) form.append('sample', sample);
  const response = await fetch(`${API_BASE_URL}${endpoint}`, { method, body: form, credentials: 'include' });
  return readJson(response);
}

export const fetchTemplates = () => request('/api/whatsapp/templates');
export const createTemplate = (data) => sendTemplateForm('POST', '/api/whatsapp/templates', data);
export const updateTemplate = (id, data) => sendTemplateForm('PUT', `/api/whatsapp/templates/${id}`, data);
export const deleteTemplate = (id) =>
  request(`/api/whatsapp/templates/${id}`, { method: 'DELETE' });
// Imports templates made in WhatsApp Manager and refreshes every status from Meta.
export const syncTemplates = () => request('/api/whatsapp/templates/sync', { method: 'POST' });

// --- Campaigns ---
export const fetchCampaigns = () => request('/api/whatsapp/campaigns');
export const fetchCampaign = (id) => request(`/api/whatsapp/campaigns/${id}`);
export const fetchCampaignMessages = (id) => request(`/api/whatsapp/campaigns/${id}/messages`);
export const createCampaign = (data) =>
  request('/api/whatsapp/campaigns', { method: 'POST', body: JSON.stringify(data) });
export const updateCampaign = (id, data) =>
  request(`/api/whatsapp/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
// Stops a queued/scheduled/sending campaign; messages not yet sent are cancelled.
export const cancelCampaign = (id) => request(`/api/whatsapp/campaigns/${id}/cancel`, { method: 'POST' });
export const sendCampaignTestMessage = (id, phones) =>
  request(`/api/whatsapp/campaigns/${id}/test-send`, { method: 'POST', body: JSON.stringify({ phones }) });

// --- Media upload (image/video/document/audio attachment) ---
export const uploadMedia = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`${API_BASE_URL}/api/whatsapp/media/upload`, { method: 'POST', body: form, credentials: 'include' });
  return readJson(response);
};

// --- Long video for "send full video on request" ---
// Returns at once with status 'processing'; poll fetchLongVideo until 'ready' (or 'failed').
export const uploadLongVideo = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`${API_BASE_URL}/api/whatsapp/media/long-video`, { method: 'POST', body: form, credentials: 'include' });
  return readJson(response);
};
export const fetchLongVideo = (id) => request(`/api/whatsapp/media/long-video/${id}`);

// --- Saved segments (shared MySQL contact audience) ---
export const fetchSavedSegments = () => request('/api/saved-segments');
export const createSavedSegment = (data) =>
  request('/api/saved-segments', { method: 'POST', body: JSON.stringify(data) });
export const deleteSavedSegment = (id) =>
  request(`/api/saved-segments/${id}`, { method: 'DELETE' });
export const previewSavedSegment = (data) =>
  request('/api/saved-segments/preview', { method: 'POST', body: JSON.stringify(data) });

// --- A/B testing ---
export const fetchAbTestMetrics = (campaignId) => request(`/api/ab-tests/campaigns/${campaignId}/abtest/metrics`);

// --- Analytics: paginated per-metric event lists + summary stats, scoped to a
// campaign/template/contact. eventType is one of: read, clicks, replies, blocked, bounced.
function buildAnalyticsParams({ page, limit, startDate, endDate } = {}) {
  const params = new URLSearchParams();
  if (page) params.set('page', String(page));
  if (limit) params.set('limit', String(limit));
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  return params.toString();
}

export const fetchCampaignAnalytics = (id, eventType, opts) =>
  request(`/api/whatsapp/campaigns/${id}/analytics/${eventType}?${buildAnalyticsParams(opts)}`);

export const fetchTemplateAnalytics = (id, eventType, opts) =>
  request(`/api/whatsapp/templates/${id}/analytics/${eventType}?${buildAnalyticsParams(opts)}`);

export const fetchContactAnalytics = (phone, eventType, opts) =>
  request(`/api/whatsapp/contacts/${encodeURIComponent(phone)}/analytics/${eventType}?${buildAnalyticsParams(opts)}`);

export const fetchTemplateStats = (id) => request(`/api/whatsapp/templates/${id}/stats`);
export const fetchContactStats = (phone) => request(`/api/whatsapp/contacts/${encodeURIComponent(phone)}/stats`);

// --- Contacts / Audience (shared CRM MySQL database) ---
export const fetchAudienceOverview = () => request('/api/audience/stats/overview');
// company: null for every contact, '' for contacts with no company, or a company name.
// filters: { column: [values] } from the column filter menus; sent as JSON so commas in names survive.
export const fetchAllContacts = ({ limit = 50, offset = 0, company = null, search = '', filters = {} } = {}) => {
  const qs = new URLSearchParams({ limit, offset });
  if (company !== null && company !== undefined) qs.set('company', company);
  if (search) qs.set('search', search);
  if (Object.keys(filters).length) qs.set('filters', JSON.stringify(filters));
  return request(`/api/audience/all?${qs}`);
};
// Every value of a contacts column (company, name, email, source) with counts, for filter menus.
// Same response shape as Email Marketing's /facets, so ColumnFilter's loadOptions can use it as is.
export const fetchContactFacets = async (column, { q = '', offset = 0 } = {}) => {
  const qs = new URLSearchParams({ column });
  if (q) qs.set('q', q);
  if (offset) qs.set('offset', String(offset));
  const { data } = await request(`/api/audience/facets?${qs}`);
  return data;
};
export const fetchCompanies = ({ limit = 25, offset = 0, search = '', filters = {} } = {}) => {
  const qs = new URLSearchParams({ limit, offset });
  if (search) qs.set('search', search);
  if (Object.keys(filters).length) qs.set('filters', JSON.stringify(filters));
  return request(`/api/audience/companies?${qs}`);
};
export const fetchAudienceCount = (audience) =>
  request('/api/audience/count', { method: 'POST', body: JSON.stringify(audience) });
export const previewAudience = (audience) =>
  request('/api/audience/preview', { method: 'POST', body: JSON.stringify(audience) });

// --- Live Inbox: conversations, ownership and handover ---
const chatPath = (phone) => `/api/whatsapp/conversations/${encodeURIComponent(phone)}`;
const post = (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) });

export const fetchConversations = ({ view = 'all', status = '', q = '' } = {}) => {
  const qs = new URLSearchParams({ view });
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  return request(`/api/whatsapp/conversations?${qs}`);
};
export const fetchUnreadSummary = () => request('/api/whatsapp/conversations/unread');
export const fetchConversation = (phone) => request(chatPath(phone));
export const fetchConversationMessages = (phone) => request(`${chatPath(phone)}/messages`);
// to: { id, name, email } to assign/transfer/claim, or null to unassign.
export const assignConversation = (phone, to, note = '') => post(`${chatPath(phone)}/assign`, { to, note });
export const setConversationStatus = (phone, status) => post(`${chatPath(phone)}/status`, { status });
export const shareConversation = (phone, withUser, shared = true) => post(`${chatPath(phone)}/share`, { with: withUser, shared });
export const addConversationNote = (phone, text) => post(`${chatPath(phone)}/notes`, { text });
export const fetchAgents = () => request('/api/whatsapp/agents');

// --- Quick replies (saved answers shared by the team) ---
export const fetchQuickReplies = () => request('/api/whatsapp/quick-replies');
export const createQuickReply = (data) => post('/api/whatsapp/quick-replies', data);
export const updateQuickReply = (id, data) => request(`/api/whatsapp/quick-replies/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteQuickReply = (id) => request(`/api/whatsapp/quick-replies/${id}`, { method: 'DELETE' });
